// frontend/src/components/EditPostModal.jsx
import { useState, useEffect } from "react";
import { X, Upload, Loader2, Trash2, Plus } from "lucide-react";
import Swal from "sweetalert2";

const categoriasOrdenadas = [
  "Artigos Infantis",
  "Aventura e Lazer",
  "Brinquedos e Bicicletas",
  "Celulares",
  "Decoração e Lar",
  "Eletro",
  "Esporte e Fitness",
  "Ferramentas, Construção e Reforma",
  "Hobbies, Musica, Arte e Livros",
  "Home Office",
  "Imóveis",
  "Informática e Games",
  "Moda e Achados de Estilo",
  "Moveis",
  "Mundo Pets",
  "Otros",
  "TV, Audio, Video e Drones",
  "Veículos e Autopeças",
];

export default function EditPostModal({ post, isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    titulo: "",
    categoria: "",
    tipo: "Venda",
    condicao: "usado", // 🔥 NUEVO
    preco: "",
    descricao: "",
  });
  const [fotos, setFotos] = useState([]);
  const [fotosRemover, setFotosRemover] = useState([]);
  const [fotosNovas, setFotosNovas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (post) {
      setFormData({
        titulo: post.titulo || "",
        categoria: post.categoria || "",
        tipo: post.tipo || "Venda",
        condicao: post.condicao || "usado", // 🔥 NUEVO
        preco: post.preco ? formatPriceForInput(post.preco) : "",
        descricao: post.descricao || "",
      });
      setFotos(post.fotos || []);
      setFotosRemover([]);
      setFotosNovas([]);
    }
  }, [post]);

  const formatPriceForInput = (price) => {
    if (!price) return "";
    return new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const formatPriceForSubmit = (price) => {
    if (!price) return "";
    return parseFloat(price.replace(/\./g, "").replace(",", "."));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "preco") {
      let numericValue = value.replace(/\D/g, "");
      if (numericValue === "") {
        setFormData({ ...formData, [name]: "" });
        return;
      }
      let number = parseFloat(numericValue) / 100;
      const formatted = number.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      setFormData({ ...formData, [name]: formatted });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const currentCount = fotos.length;
    const totalAfterAdd = currentCount + files.length;

    if (totalAfterAdd > 5) {
      Swal.fire({
        icon: "warning",
        title: "Limite de fotos",
        text: "Máximo 5 fotos por anúncio.",
        confirmButtonColor: "#449879",
      });
      return;
    }

    const validFiles = files.filter((file) => file.type.startsWith("image/"));
    if (validFiles.length !== files.length) {
      Swal.fire({
        icon: "warning",
        title: "Arquivo inválido",
        text: "Apenas imagens são permitidas.",
        confirmButtonColor: "#449879",
      });
    }

    setFotosNovas([...fotosNovas, ...validFiles]);
    const previews = validFiles.map((file) => URL.createObjectURL(file));
    setFotos([...fotos, ...previews]);
  };

  const removeExistingPhoto = (index) => {
    const photoToRemove = fotos[index];
    
    if (!photoToRemove.startsWith("blob:")) {
      setFotosRemover([...fotosRemover, photoToRemove]);
    }
    
    const newFotos = [...fotos];
    newFotos.splice(index, 1);
    setFotos(newFotos);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.titulo.trim()) newErrors.titulo = "Título é obrigatório";
    if (!formData.categoria) newErrors.categoria = "Categoria é obrigatória";
    if (!formData.descricao.trim()) newErrors.descricao = "Descrição é obrigatória";
    if (formData.tipo === "Venda" && !formData.preco) {
      newErrors.preco = "Preço é obrigatório para venda";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const formPayload = new FormData();
      formPayload.append("titulo", formData.titulo);
      formPayload.append("categoria", formData.categoria);
      formPayload.append("tipo", formData.tipo);
      formPayload.append("condicao", formData.condicao); // 🔥 NUEVO
      formPayload.append("descricao", formData.descricao);
      
      if (formData.tipo === "Venda" && formData.preco) {
        formPayload.append("preco", formatPriceForSubmit(formData.preco));
      }

      if (fotosRemover.length > 0) {
        formPayload.append("fotos_remover", JSON.stringify(fotosRemover));
      }

      fotosNovas.forEach((foto) => {
        formPayload.append("fotos[]", foto);
      });

      await onSuccess(formPayload, post.id);
      onClose();
    } catch (error) {
      console.error("Error:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: error.response?.data?.message || "Erro ao atualizar anúncio",
        confirmButtonColor: "#449879",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>

        <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">Editar anúncio</h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Título */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Título <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="titulo"
                value={formData.titulo}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449879] focus:border-transparent ${
                  errors.titulo ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.titulo && <p className="text-red-500 text-xs mt-1">{errors.titulo}</p>}
            </div>

            {/* Categoria */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria <span className="text-red-500">*</span>
              </label>
              <select
                name="categoria"
                value={formData.categoria}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449879] focus:border-transparent ${
                  errors.categoria ? "border-red-500" : "border-gray-300"
                }`}
              >
                <option value="">Selecione uma categoria</option>
                {categoriasOrdenadas.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              {errors.categoria && <p className="text-red-500 text-xs mt-1">{errors.categoria}</p>}
            </div>

            {/* 🔥 CONDICIÓN - NUEVO CAMPO */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Condição do produto <span className="text-red-500">*</span>
              </label>
              <select
                name="condicao"
                value={formData.condicao || 'usado'}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449879] focus:border-transparent bg-white"
              >
                <option value="usado">🔄 Usado</option>
                <option value="novo">🆕 Novo</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">
                Selecione se o produto é novo ou usado
              </p>
            </div>

            {/* Tipo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-4">
                <label className="flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border cursor-pointer transition-all bg-white">
                  <input
                    type="radio"
                    name="tipo"
                    value="Venda"
                    checked={formData.tipo === "Venda"}
                    onChange={handleChange}
                    className="hidden"
                  />
                  <span className={formData.tipo === "Venda" ? "text-green-600 font-medium" : "text-gray-500"}>
                    Venda
                  </span>
                </label>
                <label className="flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border cursor-pointer transition-all bg-white">
                  <input
                    type="radio"
                    name="tipo"
                    value="Doação"
                    checked={formData.tipo === "Doação"}
                    onChange={handleChange}
                    className="hidden"
                  />
                  <span className={formData.tipo === "Doação" ? "text-blue-600 font-medium" : "text-gray-500"}>
                    Doação
                  </span>
                </label>
              </div>
            </div>

            {/* Precio */}
            {formData.tipo === "Venda" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preço <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="preco"
                  value={formData.preco}
                  onChange={handleChange}
                  placeholder="R$ 0,00"
                  className={`w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449879] focus:border-transparent ${
                    errors.preco ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.preco && <p className="text-red-500 text-xs mt-1">{errors.preco}</p>}
              </div>
            )}

            {/* Descrição */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição <span className="text-red-500">*</span>
              </label>
              <textarea
                name="descricao"
                value={formData.descricao}
                onChange={handleChange}
                rows={4}
                className={`w-full px-4 py-2 border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#449879] focus:border-transparent ${
                  errors.descricao ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.descricao && <p className="text-red-500 text-xs mt-1">{errors.descricao}</p>}
            </div>

            {/* Fotos */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fotos <span className="text-xs text-gray-400">(máximo 5)</span>
              </label>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                {fotos.map((foto, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={foto}
                      alt={`Foto ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeExistingPhoto(index)}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-90 hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>

              {fotos.length < 5 && (
                <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-[#449879] transition-colors">
                  <Plus className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-500">Adicionar foto</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              )}
              <p className="text-xs text-gray-400 mt-2">
                Formatos: JPG, PNG. Máx 2MB por foto.
                {fotosRemover.length > 0 && (
                  <span className="block text-amber-600 mt-1">
                    ⚠️ {fotosRemover.length} foto(s) serão removidas ao salvar.
                  </span>
                )}
              </p>
            </div>

            {/* Botones */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-xl font-medium hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#449879] text-white py-2 rounded-xl font-medium hover:bg-[#2d5f4d] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {loading ? "Salvando..." : "Salvar alterações"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}